import { useEffect, type ReactElement } from 'react';
import { Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { useThemeColors, Icon } from '@kiips/ui';
import type {
  AffiliateProduct,
  AffiliateSurface,
} from '@my-fullstack-app/affiliates';
import type { Journey, Stay } from '@my-fullstack-app/itinerary-types';
import {
  getAffiliateProvider,
  useAffiliatesEnabled,
} from '../../lib/affiliates/provider';
import {
  trackAffiliateClickAndOpen,
  trackAffiliateImpression,
} from '../../lib/affiliates/track-and-open';
import {
  appendSubId,
  buildSubId,
  useAffiliateDefaults,
  type AffiliateDefaults,
} from '../../lib/affiliates/contextual-defaults';
import { RAIL_ICONS } from '../../lib/affiliates/rail-modes';

interface BaseProps {
  tripId: string;
  /** Forwarded to every builder as `adults`. Typically
   *  `Trip.participants?.length`. */
  participantCount?: number | undefined;
}

interface StayProps extends BaseProps {
  kind: 'stay';
  stay: Stay;
}

interface JourneyProps extends BaseProps {
  kind: 'journey';
  journey: Journey;
}

type Props = StayProps | JourneyProps;

interface RenderableCta {
  icon: IconName;
  label: string;
  surface: AffiliateSurface;
  product: AffiliateProduct;
  entityKind: 'stay' | 'journey';
  entityId: string;
  tripId: string;
  context: string;
  url: string;
}

/**
 * "Find a deal" CTA rendered in the booking section of Journey and Stay
 * detail screens. Hidden when:
 *   - the affiliate provider is Noop (marker env unset),
 *   - the entity is missing the data we'd pass to the partner (e.g. no
 *     IATA codes on a flight, no checkIn on a stay),
 *   - the journey mode has no matching deep-link builder. Today's
 *     coverage spans all six Journey modes: flight (Aviasales),
 *     car-rental (Discover Cars), transfer (Kiwi Taxi), and
 *     train / bus / ferry (Omio — see ADR-0066).
 *
 * Fires `affiliate_impression` once per mount when rendered, then
 * `affiliate_click` on tap. See ADR-0064.
 */
export function AffiliateSearchButton(props: Props): ReactElement | null {
  const theme = useThemeColors();
  // Reactive consent gate — re-renders the CTA hidden when the user
  // withdraws affiliate consent mid-session (ADR-0065).
  const enabled = useAffiliatesEnabled();
  const defaults = useAffiliateDefaults(props.participantCount);
  const cta = enabled ? resolveCta(props, defaults) : null;

  // Hooks must run unconditionally — keep the useEffect before any early
  // return. The guard inside short-circuits when there's no CTA to render.
  // Dep list is primitive so the effect fires once per (entity, surface)
  // and not on every render (the resolved cta object is fresh each call).
  const entityId = cta?.entityId;
  const surface = cta?.surface;
  const product = cta?.product;
  const entityKind = cta?.entityKind;
  const tripId = cta?.tripId;
  const context = cta?.context;
  useEffect(() => {
    if (!entityId || !surface || !product || !entityKind || !tripId) return;
    trackAffiliateImpression({
      provider: getAffiliateProvider().id,
      surface,
      product,
      entityKind,
      entityId,
      tripId,
      context,
    });
  }, [entityId, surface, product, entityKind, tripId, context]);

  if (!cta) return null;

  return (
    <ButtonShell
      icon={cta.icon}
      label={cta.label}
      tone={theme.textOnSurface}
      onPress={() =>
        void trackAffiliateClickAndOpen({
          provider: getAffiliateProvider().id,
          surface: cta.surface,
          product: cta.product,
          entityKind: cta.entityKind,
          entityId: cta.entityId,
          tripId: cta.tripId,
          context: cta.context,
          url: cta.url,
        })
      }
    />
  );
}

/** Pure resolver: turns the entity into a renderable CTA descriptor or
 *  null. Provider + entity-shape gating lives here. The caller MUST
 *  have already verified consent via `useAffiliatesEnabled` — this
 *  function trusts that and skips the consent check so it can stay
 *  hook-free (callable from `useEffect` impression paths, etc.). */
function resolveCta(props: Props, defaults: AffiliateDefaults): RenderableCta | null {
  const provider = getAffiliateProvider();
  if (provider.id === 'noop') return null;

  if (props.kind === 'stay') {
    const { stay, tripId } = props;
    const destinationName = (stay.customLabel || stay.place.name).trim();
    const checkIn = stay.checkIn.slice(0, 10);
    const checkOut = stay.checkOut.slice(0, 10);
    if (!destinationName || !checkIn || !checkOut) return null;
    const built = provider.buildHotelSearchUrl({
      destinationName,
      checkIn,
      checkOut,
      adults: defaults.adults,
      currency: defaults.currency,
      locale: defaults.locale,
    });
    const url = appendSubId(built, buildSubId('stay_row', 'hotel'));
    if (!url) return null;
    return {
      icon: 'hotel',
      label: 'Find this hotel',
      surface: 'stay_row',
      product: 'hotel',
      entityKind: 'stay',
      entityId: stay.id,
      tripId,
      context: destinationName,
      url,
    };
  }

  // Journey: per-mode resolution.
  const { journey, tripId } = props;
  const originName = journeyAnchorName(journey.origin);
  const destName = journeyAnchorName(journey.destination);
  const departDate = journey.origin.time.slice(0, 10);

  if (journey.mode === 'flight') {
    const originIata = journey.origin.code?.toUpperCase().trim() ?? '';
    const destIata = journey.destination.code?.toUpperCase().trim() ?? '';
    if (!originIata || !destIata || !departDate) return null;
    const built = provider.buildFlightSearchUrl({
      originIata,
      destinationIata: destIata,
      departDate,
      adults: defaults.adults,
      currency: defaults.currency,
      locale: defaults.locale,
    });
    const url = built ? appendSubId(built, buildSubId('journey_row', 'flight')) : null;
    if (!url) return null;
    return {
      icon: 'flight',
      label: 'Find this flight',
      surface: 'journey_row',
      product: 'flight',
      entityKind: 'journey',
      entityId: journey.id,
      tripId,
      context: `${originIata}-${destIata}`,
      url,
    };
  }

  if (journey.mode === 'car-rental') {
    const dropoffDate = journey.destination.time.slice(0, 10) || departDate;
    if (!originName || !destName || !departDate) return null;
    // Call methods via `provider.buildX?.(input)` (not pulled into a
    // local) so class-based providers like TravelPayoutsProvider keep
    // `this` bound to their instance. Pulling the method into a local
    // variable would silently null out `this.marker` at call time.
    const built = provider.buildCarRentalSearchUrl?.({
      pickupLocation: originName,
      dropoffLocation: destName,
      pickupDate: departDate,
      dropoffDate,
      currency: defaults.currency,
      locale: defaults.locale,
    });
    if (built == null) return null;
    const url = appendSubId(built, buildSubId('journey_row', 'car_rental'));
    if (!url) return null;
    return {
      icon: 'directions-car',
      label: 'Find this car rental',
      surface: 'journey_row',
      product: 'car_rental',
      entityKind: 'journey',
      entityId: journey.id,
      tripId,
      context: `${originName} → ${destName}`,
      url,
    };
  }

  if (journey.mode === 'transfer') {
    if (!originName || !destName || !departDate) return null;
    const built = provider.buildAirportTransferUrl?.({
      originName,
      destinationName: destName,
      date: departDate,
      passengers: defaults.adults,
      currency: defaults.currency,
      locale: defaults.locale,
    });
    if (built == null) return null;
    const url = appendSubId(built, buildSubId('journey_row', 'transfer'));
    if (!url) return null;
    return {
      icon: 'local-taxi',
      label: 'Find a transfer',
      surface: 'journey_row',
      product: 'transfer',
      entityKind: 'journey',
      entityId: journey.id,
      tripId,
      context: `${originName} → ${destName}`,
      url,
    };
  }

  // Rail / bus / ferry share one builder (Omio) with a mode hint.
  // Distinct product values let analytics decompose by mode. See
  // ADR-0066. `journey.mode` is narrowed to 'train' | 'bus' | 'ferry'
  // here by elimination of the prior branches — the explicit RAIL_MODES
  // map keys force a compile-time error if a new Journey mode is added
  // without giving it a CTA.
  if (!originName || !destName || !departDate) return null;
  const railLabels: Record<'train' | 'bus' | 'ferry', string> = {
    train: 'Find this train',
    bus: 'Find this bus',
    ferry: 'Find this ferry',
  };
  const built = provider.buildTrainSearchUrl?.({
    originName,
    destinationName: destName,
    departDate,
    mode: journey.mode,
    passengers: defaults.adults,
    currency: defaults.currency,
    locale: defaults.locale,
  });
  if (built == null) return null;
  const url = appendSubId(built, buildSubId('journey_row', journey.mode));
  if (!url) return null;
  return {
    icon: RAIL_ICONS[journey.mode],
    label: railLabels[journey.mode],
    surface: 'journey_row',
    product: journey.mode,
    entityKind: 'journey',
    entityId: journey.id,
    tripId,
    context: `${originName} → ${destName}`,
    url,
  };
}

/** Display name for a JourneyAnchor — prefers the user's customLabel,
 *  falls back to the picked place name. IATA / station code is appended
 *  when present so the partner page lands on the right airport (e.g.
 *  "JFK John F. Kennedy International Airport" rather than the city). */
function journeyAnchorName(
  anchor: Journey['origin'] | Journey['destination'],
): string {
  const base = (anchor.customLabel || anchor.place.name || '').trim();
  const code = anchor.code?.trim();
  if (base && code) return `${code} ${base}`;
  return base || code || '';
}

function ButtonShell({
  icon,
  label,
  tone,
  onPress,
}: {
  icon: IconName;
  label: string;
  tone: string;
  onPress: () => void;
}) {
  return (
    <YStack marginTop={6} gap={6}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
        style={({ pressed }) => [pressed ? { transform: [{ scale: 0.98 }] } : null]}
      >
        <XStack
          backgroundColor="$surfaceCanvas"
          borderRadius={12}
          paddingHorizontal={12}
          paddingVertical={10}
          alignItems="center"
          gap={8}
        >
          <Icon name={icon} size={18} color={tone} />
          <Text fontSize={13} fontWeight="600" color="$textOnSurface" flex={1} numberOfLines={1}>
            {label}
          </Text>
          <Icon name="open-in-new" size={14} color={tone} />
        </XStack>
      </Pressable>
      <Text fontSize={11} color="$textSubdued" lineHeight={14}>
        Affiliate link — Kiips earns a commission at no extra cost to you.
      </Text>
    </YStack>
  );
}
